import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Chip, Link, Stack, Typography } from '@mui/material';
import { Tour } from '@mui/icons-material';

import { CircuitOffer, itineraryCityChain } from '../circuits';
import { useCircuitProduct } from '../queries';
import { CircuitItineraryChain } from './CircuitItineraryChain';

interface Props {
  offer: CircuitOffer;
  onOpenSheet: () => void;
  /** Volver al descubrimiento: soltar el circuito elegido, no la búsqueda. */
  onChangeCircuit: () => void;
}

/**
 * El circuito buscado, en una banda compacta arriba de sus salidas. Todas las
 * filas son del MISMO producto (la búsqueda es dirigida), así que lo que es del
 * producto vive acá una vez: el mapa en miniatura, el nombre, el recorrido y
 * los sellos comerciales.
 *
 * **Los sellos son de PRODUCTO, no de salida**: salida garantizada, "a
 * confirmar por operador" y pasaporte los publica la ficha, no cada fecha —
 * repetirlos por fila los convertiría en ruido y sugeriría que varían por
 * salida, que es lo que el contrato NO dice. Por eso el encabezado lee la ficha
 * (cacheada como catálogo: si el asesor llegó por la ficha, ya está en memoria).
 *
 * El aviso de pasaporte va acá, ANTES de elegir salida: si no, el proveedor
 * rechaza la reserva al final y el pasajero ya completó todo.
 */
export function CircuitProductHeader({
  offer,
  onOpenSheet,
  onChangeCircuit,
}: Props) {
  const { t } = useTranslation();
  const [imageFailed, setImageFailed] = useState(false);
  const { data: product } = useCircuitProduct(offer.productRef, offer.provider);

  const image = imageFailed ? undefined : offer.images?.[0] ?? product?.images?.[0];
  const chain = itineraryCityChain(product?.itinerary);
  const durationDays = product?.durationDays ?? offer.durationDays;
  const flags = [
    product?.guaranteedDeparture && {
      key: 'guaranteed',
      color: 'success' as const,
      label: t('travelServices.circuits.guaranteedDeparture'),
    },
    product?.immediateConfirmation === false && {
      key: 'onRequest',
      color: 'warning' as const,
      label: t('travelServices.circuits.toConfirmByOperator'),
    },
    // El pasaporte lo declara la ficha y también la oferta (viaja en la
    // declaración de campos de pasajero): alcanza que lo diga uno de los dos.
    (product?.passportRequired || offer.passportRequired) && {
      key: 'passport',
      color: 'info' as const,
      label: t('travelServices.circuits.passportFlag'),
    },
  ].filter(Boolean) as {
    key: string;
    color: 'success' | 'warning' | 'info';
    label: string;
  }[];

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        flexWrap: 'wrap',
        px: 1.75,
        py: 1.25,
      }}
    >
      <Box
        width={64}
        height={46}
        borderRadius={1}
        bgcolor="grey.100"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        overflow="hidden"
      >
        {image ? (
          <Box
            component="img"
            src={image}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <Tour sx={{ fontSize: 24 }} color="action" />
        )}
      </Box>

      <Box flex={1} minWidth={220}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
          {offer.title ?? product?.name}
        </Typography>
        {chain.stops.length > 0 ? (
          <CircuitItineraryChain
            stops={chain.stops}
            withDays={chain.withDays}
            tail={[
              chain.truncated ? '…' : undefined,
              durationDays
                ? t('travelServices.durationDays', { count: durationDays })
                : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
          />
        ) : (
          // Mientras la ficha carga (o si el proveedor no publica itinerario)
          // queda lo que la oferta sí trae, sin dejar el hueco de una línea.
          durationDays && (
            <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
              {t('travelServices.durationDays', { count: durationDays })}
            </Typography>
          )
        )}
      </Box>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
        {flags.map((flag) => (
          <Chip
            key={flag.key}
            size="small"
            color={flag.color}
            variant="outlined"
            label={flag.label}
            sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
          />
        ))}
        {offer.productRef && (
          <Link
            component="button"
            type="button"
            underline="hover"
            sx={{ fontSize: 12, ml: 0.5 }}
            onClick={onOpenSheet}
          >
            {t('travelServices.circuits.viewSheet')}
          </Link>
        )}
        <Link
          component="button"
          type="button"
          underline="hover"
          sx={{ fontSize: 12 }}
          onClick={onChangeCircuit}
        >
          {t('travelServices.circuits.changeCircuit')}
        </Link>
      </Stack>
    </Card>
  );
}
